import { useMemo, useState } from "react";
import { InventoryHeaderInterface } from "../../interfaces/InventoryInterface";
import { FormTextInput, FormMultipleSimpleRadioGroup, MultipleRadioOption } from "../../components/FormFields";
import moment from "moment";
import PaginationFooter from "../../components/PaginationFooter";
import Modal from "../../components/Modal";
import InventoryItemsZeroSquare from "./InventoryItemsZeroSquare";

interface InventoryHeaderTableProps {
  inventorys: InventoryHeaderInterface[];
  onInventoryClick: (manifest: InventoryHeaderInterface) => void;
}

const ROW_PER_PAGES = 10;

const InventoryHeaderTable: React.FC<InventoryHeaderTableProps> = ({
  inventorys,
  onInventoryClick,
}) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedInventories, setSelectedInventories] = useState<string[]>([]);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);

  const toggleSelection = (id: string | undefined) => {
    if (!id) return;
    setSelectedInventories(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const filteredInventory = useMemo(() => {
    const tokens = search.toLowerCase().split(" ");

    return inventorys
      .filter((m) =>
        tokens.every(
          (t) =>
            m.inventoryHeaderCode?.toLowerCase().includes(t) ||
            m.statusName?.toLowerCase().includes(t)
        )
      )
      .sort((a, b) =>
        moment(a.inventoryOpenDate) < moment(b.inventoryOpenDate) ? 1 : -1
      );
  }, [inventorys, search]);

  const pageCount = useMemo(() => {
    return Math.ceil(filteredInventory.length / ROW_PER_PAGES);
  }, [filteredInventory]);

  const offset = useMemo(() => currentPage * ROW_PER_PAGES, [currentPage]);

  const filteredInventoryPage = useMemo(
    () => filteredInventory.slice(offset, offset + ROW_PER_PAGES),
    [filteredInventory, offset]
  );

  const handlePageClick = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected);
  };

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="flex flex-col max-w-[40rem]">
        <FormTextInput
          label="Filtrar"
          placeholder="Por codigo de inventario o estatus"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredInventory.length === 0 && (
        <p className="text-gray-400 text-center my-16">
          No hay inventarios en esta lista
        </p>
      )}

      {filteredInventory.length > 0 && (
        <div className="overflow-x-auto pl-2">
          <table className="table-auto w-full">
            <thead>
              <tr className="text-center">
                <th className="px-4 py-2 font-semibold text-xs">INVENTARIO</th>
                <th className="px-4 py-2 font-semibold text-xs">ESTATUS</th>
                <th className="px-4 py-2 font-semibold text-xs">
                  FECHA APERTURA
                </th>
                <th className="px-4 py-2 font-semibold text-xs">
                  FECHA CIERRE
                </th>
                <th className="px-4 py-2 font-semibold text-xs">TOTAL GUIAS</th>
                <th className="px-4 py-2 font-semibold text-xs">TOTAL PIEZAS</th>
                <th className="px-4 py-2 font-semibold text-xs">TOTAL PESO</th>
                <th className="px-4 py-2 font-semibold text-xs">FECHA CREACIÓN</th>
                <th className="px-4 py-2 font-semibold text-xs"></th>
              </tr>
            </thead>

            <tbody>
              {filteredInventoryPage.map((m, index) => (
                <tr
                  key={m.inventoryHeaderID}
                  onClick={() => onInventoryClick(m)}
                  className={`cursor-pointer hover:bg-gray-100 text-center ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {m.inventoryHeaderCode?.toUpperCase()}
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {m.statusName?.toUpperCase()}
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {moment(m.inventoryOpenDate).format("DD/MM/YYYY hh:mm:ss A")}
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                   {m.inventoryClosedDate
                      ? moment(m.inventoryClosedDate).format(
                          "DD/MM/YYYY hh:mm:ss A"
                        )
                      : "-"}
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {m.numberOfShipments ?? 0 }
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {m.totalPieces ?? 0 }
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {m.totalWeight?.toFixed(2) ?? 0 }
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
                    {moment(m.creationDate).format("DD/MM/YYYY hh:mm:ss A")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <PaginationFooter
            rowCounts={ROW_PER_PAGES}
            itemsOffSet={offset}
            totalPages={pageCount}
            totalItems={filteredInventory.length}
            handlePageClick={handlePageClick}
          />
        </div>
      )}
    </div>
  );
};

export default InventoryHeaderTable;